import {
  IS_PROD,
  GA4_MEASUREMENT_ID,
  GOOGLE_ADS_TAG_ID,
  GOOGLE_MEASUREMENT_API_SECRET,
  GOOGLE_ADS_CONVERSION_LABEL,
  GOOGLE_ADS_CONVERSION_LABELS,
} from '@/lib/constant'
import type { ConversionEventData, ConversionEventType } from './types'
import { CONVERSION_EVENT_TYPES } from './types'

/**
 * Google Analytics 4 Measurement Protocol
 */
export async function sendToGoogleAnalytics(
  eventData: ConversionEventData,
): Promise<void> {
  // Prefer client-side gtag when available to keep client_id/session attribution
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      sendToGoogleAnalyticsClient(eventData)
      return
    } catch (error) {
      console.error('Fallback to Measurement Protocol after gtag error:', error)
    }
  }

  // In non-prod environments, still send events but mark them for debugging
  // This allows testing tracking functionality in staging
  if (!IS_PROD) {
    console.log('GA4 Event (non-prod/staging):', eventData)
    // Continue to send events even in non-prod for debugging purposes
    // You can filter these events in GA4 using debug_mode or other dimensions
  }

  try {
    const apiSecret = GOOGLE_MEASUREMENT_API_SECRET
    if (!apiSecret) {
      console.warn('GOOGLE_MEASUREMENT_API_SECRET not configured')
      return
    }

    // 映射事件名称到GA4标准事件
    const gaEventMap: Record<ConversionEventType, string> = {
      [CONVERSION_EVENT_TYPES.SIGNUP]: 'sign_up',
      [CONVERSION_EVENT_TYPES.REFERRAL_SIGNUP]: 'referral_signup',
      [CONVERSION_EVENT_TYPES.LOGIN]: 'login',
      [CONVERSION_EVENT_TYPES.START_CODING]: 'begin_coding',
      [CONVERSION_EVENT_TYPES.FINISH_CODING]: 'complete_coding',
      [CONVERSION_EVENT_TYPES.CHECKOUT]: 'begin_checkout',
      [CONVERSION_EVENT_TYPES.ORDER_SUCCESS]: 'purchase',
      [CONVERSION_EVENT_TYPES.SUBSCRIBE_SUCCESS]: 'purchase',
    }

    const gaEventName = gaEventMap[eventData.event_type] || eventData.event_type

    // 严格过滤掉所有 PII 数据，只发送安全的非 PII 数据给 Google Analytics
    const {
      user_email,
      ip_address,
      user_agent,
      screen_width,
      screen_height,
      reddit_click_id,
      reddit_uuid,
      social_fbp,
      social_fbc,
      conversion_id,
      ga_client_id,
      ga_session_id,
      ga_session_number,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      gclid,
      wbraid,
      gbraid,
      page_url,
      referrer_url,
      ...safeData
    } = eventData

    const fallbackClientId = `server.${eventData.user_id || Date.now()}`
    const clientId = ga_client_id || eventData.user_id || fallbackClientId

    const eventParams: Record<string, any> = {
      ...safeData,
      user_id: eventData.user_id,
      engagement_time_msec: eventData.engagement_time_msec || 1,
    }

    if (page_url) eventParams.page_location = page_url
    if (referrer_url) eventParams.page_referrer = referrer_url
    if (ga_session_id) eventParams.ga_session_id = ga_session_id
    if (ga_session_number) eventParams.ga_session_number = ga_session_number
    if (gclid) eventParams.gclid = gclid
    if (wbraid) eventParams.wbraid = wbraid
    if (gbraid) eventParams.gbraid = gbraid
    if (utm_campaign) eventParams.campaign = utm_campaign
    if (utm_source) eventParams.source = utm_source
    if (utm_medium) eventParams.medium = utm_medium
    if (utm_term) eventParams.term = utm_term
    if (utm_content) eventParams.content = utm_content

    const trafficSource: Record<string, any> = {}
    if (utm_source) trafficSource.source = utm_source
    if (utm_medium) trafficSource.medium = utm_medium
    if (utm_campaign) trafficSource.name = utm_campaign

    const payload: Record<string, any> = {
      client_id: clientId,
      user_id: eventData.user_id,
      events: [
        {
          name: gaEventName,
          params: eventParams,
          ...(eventData.event_id ? { event_id: eventData.event_id } : {}),
        },
      ],
    }

    if (Object.keys(trafficSource).length > 0) {
      payload.traffic_source = trafficSource
    }

    // Use GA4_MEASUREMENT_ID for server-side GA4 events
    // This should be the GA4 Measurement ID (G-XXXXXXXXXX), not the Google Ads Tag ID
    const response = await fetch(
      `https://www.google-analytics.com/mp/collect?measurement_id=${GA4_MEASUREMENT_ID}&api_secret=${apiSecret}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      console.error(
        'Failed to send event to Google Analytics:',
        response.statusText,
      )
    }
  } catch (error) {
    console.error('Error sending event to Google Analytics:', error)
  }
}

/**
 * Send GA4 event via client-side gtag (preferred for attribution)
 */
export function sendToGoogleAnalyticsClient(
  eventData: ConversionEventData,
): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    console.warn('gtag not available on client, skipping GA4 client event')
    return
  }

  const gaEventMap: Record<ConversionEventType, string> = {
    [CONVERSION_EVENT_TYPES.SIGNUP]: 'sign_up',
    [CONVERSION_EVENT_TYPES.REFERRAL_SIGNUP]: 'referral_signup',
    [CONVERSION_EVENT_TYPES.LOGIN]: 'login',
    [CONVERSION_EVENT_TYPES.START_CODING]: 'begin_coding',
    [CONVERSION_EVENT_TYPES.FINISH_CODING]: 'complete_coding',
    [CONVERSION_EVENT_TYPES.CHECKOUT]: 'begin_checkout',
    [CONVERSION_EVENT_TYPES.ORDER_SUCCESS]: 'purchase',
    [CONVERSION_EVENT_TYPES.SUBSCRIBE_SUCCESS]: 'purchase',
  }

  // Remove PII and server-only fields before sending to GA
  const {
    user_email,
    ip_address,
    user_agent,
    reddit_click_id,
    reddit_uuid,
    social_fbp,
    social_fbc,
    conversion_id,
    ga_client_id,
    ga_session_id,
    ga_session_number,
    gclid,
    wbraid,
    gbraid,
    skip_server_ga,
    ...safeData
  } = eventData

  const gaEventName = gaEventMap[eventData.event_type] || eventData.event_type

  const params: Record<string, any> = {
    ...safeData,
    user_id: eventData.user_id,
    engagement_time_msec: eventData.engagement_time_msec || 1,
  }

  if (eventData.page_url) params.page_location = eventData.page_url
  if (eventData.referrer_url) params.page_referrer = eventData.referrer_url
  if (eventData.ga_session_id) params.ga_session_id = eventData.ga_session_id
  if (eventData.ga_session_number)
    params.ga_session_number = eventData.ga_session_number

  // gtag will handle client_id/session automatically, so we avoid manually setting it
  if (eventData.event_id) {
    window.gtag('event', gaEventName, {
      ...params,
      event_id: eventData.event_id,
    })
  } else {
    window.gtag('event', gaEventName, params)
  }
}

/**
 * Send Google Ads conversion event (client-side via gtag.js)
 * This should be called from the browser after payment success
 *
 * Note: This is a helper function for client-side use. For server-side,
 * use sendToGoogleAnalytics which sends to GA4, and Google Ads will
 * automatically receive the conversion if properly configured in Google Ads.
 *
 * @param transactionId - Transaction/subscription ID for deduplication
 * @param value - Conversion value (amount)
 * @param currency - Currency code (default: 'USD')
 * @param userEmail - User email for enhanced conversions (will be hashed)
 * @param conversionLabel - Conversion label from Google Ads (e.g., "AbC123XyZ")
 *                          If not provided, falls back to GOOGLE_ADS_CONVERSION_LABEL constant
 * @param eventType - Optional event type to automatically select the correct conversion label
 */
export async function sendGoogleAdsConversion(
  transactionId: string,
  value: number,
  currency = 'USD',
  userEmail?: string,
  conversionLabel?: string,
  eventType?: ConversionEventType,
): Promise<void> {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    console.warn('gtag not available or not in browser context')
    return
  }

  // Determine conversion label based on event type or use provided label
  let label = conversionLabel

  if (!label && eventType) {
    // Auto-select conversion label based on event type
    switch (eventType) {
      case CONVERSION_EVENT_TYPES.SIGNUP:
      case CONVERSION_EVENT_TYPES.REFERRAL_SIGNUP:
        label = GOOGLE_ADS_CONVERSION_LABELS.SIGNUP
        break
      case CONVERSION_EVENT_TYPES.CHECKOUT:
        label = GOOGLE_ADS_CONVERSION_LABELS.BEGIN_CHECKOUT
        break
      case CONVERSION_EVENT_TYPES.ORDER_SUCCESS:
      case CONVERSION_EVENT_TYPES.SUBSCRIBE_SUCCESS:
        label = GOOGLE_ADS_CONVERSION_LABELS.SUBSCRIBE_PURCHASE
        break
      default:
        // For other events, use default label if configured
        label = GOOGLE_ADS_CONVERSION_LABEL
    }
  }

  // Fall back to default constant if still no label
  if (!label) {
    label = GOOGLE_ADS_CONVERSION_LABEL
  }

  if (!label) {
    console.warn(
      'Google Ads conversion label not provided and GOOGLE_ADS_CONVERSION_LABEL constant not configured',
    )
    return
  }

  try {
    // Import hash functions dynamically to avoid server-side issues
    const { hashEmail } = await import('./utils')

    // Set enhanced conversion data if email is provided
    if (userEmail) {
      const hashedEmail = await hashEmail(userEmail)
      if (hashedEmail) {
        window.gtag('set', 'user_data', {
          email: hashedEmail,
        })
      }
    }

    // Send conversion event
    // Use GOOGLE_ADS_TAG_ID (AW ID) instead of GOOGLE_MEASUREMENT_ID
    // because Google Ads conversions require AW ID format: "AW-{ACCOUNT_ID}/{LABEL}"
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_TAG_ID}/${label}`,
      value,
      currency,
      transaction_id: transactionId,
    })

    console.log('Google Ads conversion tracked:', {
      conversionLabel: label,
      transaction_id: transactionId,
      value,
      currency,
      enhanced: !!userEmail,
    })
  } catch (error) {
    console.error('Failed to track Google Ads conversion:', error)
  }
}
